import React, { useState, useEffect } from 'react';
import { api } from './api';
import './Sidebar.css';

// Отдельные страницы, не связанные с лентой постов: на них список
// разделов только отвлекает, поэтому он показывается лишь в ленте
const SERVICE_PAGES = ['classrooms', 'schedule', 'links', 'trash'];

export default function Sidebar({ 
  selectedSection, 
  selectedSubcategory,
  onSectionSelect, 
  onSubcategorySelect,
  isOpen = false,
  user = null
}) {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    loadSections();
  }, []);

  const loadSections = async () => {
    try {
      const data = await api.getSections();
      setSections(data);
      // Раскрываем первый раздел по умолчанию
      if (data.length > 0) {
        setExpandedSections({ [data[0].id]: true });
      }
    } catch (error) {
      console.error('Failed to load sections:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const showSections = !SERVICE_PAGES.includes(selectedSection?.id);

  if (loading) {
    return (
      <aside className="sidebar">
        <div className="sidebar-loading">Загрузка...</div>
      </aside>
    );
  }

  return (
    <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
      <div className="sidebar-header">
        <h2 className="sidebar-title">Разделы</h2>
      </div>
      
      <nav className="sidebar-nav">
        <button
          className={`nav-item ${!selectedSection && !selectedSubcategory ? 'active' : ''}`}
          onClick={() => {
            onSectionSelect(null);
            onSubcategorySelect(null);
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
            <polyline points="9 22 9 12 15 12 15 22"></polyline>
          </svg>
          Главная
        </button>

        <button
          className={`nav-item ${selectedSection?.id === 'classrooms' ? 'active' : ''}`}
          onClick={() => {
            onSectionSelect({ id: 'classrooms', title: 'Кабинеты' });
            onSubcategorySelect(null);
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 21h18"></path>
            <path d="M5 21V7l7-4 7 4v14"></path>
            <path d="M9 21v-5h6v5"></path>
            <path d="M9 10h.01"></path>
            <path d="M15 10h.01"></path>
          </svg>
          Кабинеты
        </button>

        <button
          className={`nav-item ${selectedSection?.id === 'schedule' ? 'active' : ''}`}
          onClick={() => {
            onSectionSelect({ id: 'schedule', title: 'Расписание' });
            onSubcategorySelect(null);
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
          Расписание
        </button>

        <button
          className={`nav-item ${selectedSection?.id === 'links' ? 'active' : ''}`}
          onClick={() => {
            onSectionSelect({ id: 'links', title: 'Полезные ссылки' });
            onSubcategorySelect(null);
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
          </svg>
          Полезные ссылки
        </button>

        {(user?.role === 'admin' || user?.is_admin || user?.is_superuser) && (
          <button
            className={`nav-item ${selectedSection?.id === 'trash' ? 'active' : ''}`}
            onClick={() => {
              onSectionSelect({ id: 'trash', title: 'Корзина' });
              onSubcategorySelect(null);
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
            Корзина
          </button>
        )}

        {showSections && sections.map(section => (
          <div key={section.id} className="section-group">
            <button
              className={`section-header ${selectedSection?.id === section.id ? 'active' : ''}`}
              onClick={() => {
                toggleSection(section.id);
                onSectionSelect(section);
                onSubcategorySelect(null);
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {section.title}
                {section.admin_only && (
                  <span className="admin-only-icon" title="Этот раздел доступен только для администраторов">
                    <svg 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2"
                    >
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  </span>
                )}
              </span>
              <svg 
                className={`section-arrow ${expandedSections[section.id] ? 'expanded' : ''}`}
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            
            {expandedSections[section.id] && section.subcategories && section.subcategories.length > 0 && (
              <div className="subcategories-list">
                {section.subcategories.map(subcategory => (
                  <button
                    key={subcategory.id}
                    className={`subcategory-item ${
                      selectedSubcategory?.id === subcategory.id ? 'active' : ''
                    }`}
                    onClick={() => {
                      onSectionSelect(section);
                      onSubcategorySelect(subcategory);
                    }}
                  >
                    {subcategory.title}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>
    </aside>
  );
}

